import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface Option {
	value: string;
	label: string;
}

interface CustomSelectProps {
	value: string;
	onChange: (value: string) => void;
	options: Option[];
	placeholder?: string;
	className?: string;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
	value,
	onChange,
	options,
	placeholder = 'Select...',
	className = '',
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [openUpward, setOpenUpward] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Check available space and decide direction when opening
	useEffect(() => {
		if (isOpen && containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			const spaceBelow = window.innerHeight - rect.bottom;
			const spaceAbove = rect.top;
			const dropdownHeight = 240; // max-h-60 = 240px

			// Open upward if not enough space below and more space above
			setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
		}
	}, [isOpen]);

	const selectedOption = options.find((opt) => opt.value === value);

	const handleSelect = (optionValue: string) => {
		onChange(optionValue);
		setIsOpen(false);
	};

	return (
		<div ref={containerRef} className={`relative ${className}`}>
			{/* Selected Value Display */}
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-400 bg-white transition-colors text-left flex items-center justify-between cursor-pointer hover:border-gray-400"
				title={selectedOption ? selectedOption.label : placeholder}>
				<span className="text-gray-900 truncate pr-2">{selectedOption ? selectedOption.label : placeholder}</span>
				<ChevronDown
					className={`h-4 w-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
				/>
			</button>

			{/* Dropdown Menu - Completely Opaque with solid white background */}
			{isOpen && (
				<div
					ref={dropdownRef}
					className={`absolute z-50 w-full min-w-max border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto ${
						openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
					}`}
					style={{
						backgroundColor: '#ffffff',
						opacity: 1,
					}}>
					{options.map((option) => (
						<div
							key={option.value}
							onClick={() => handleSelect(option.value)}
							className={`px-3 py-2 cursor-pointer text-sm transition-colors first:rounded-t-lg last:rounded-b-lg flex items-center justify-between ${
								option.value === value ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-700 hover:bg-gray-50'
							}`}
							style={{
								backgroundColor: option.value === value ? '#f3f4f6' : '#ffffff',
							}}
							title={option.label}>
							<span className="flex-1 truncate pr-2">{option.label}</span>
							{option.value === value && <span className="text-green-600 flex-shrink-0">✓</span>}
						</div>
					))}
				</div>
			)}
		</div>
	);
};
